import { Suspense } from "react";
import { EVOHR_LOGO } from "@/lib/logo";
import { LanguageProvider } from "@/context/LanguageContext";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { ThemeProvider } from "next-themes";
import { LazyErrorBoundary } from "@/components/shared/LazyErrorBoundary";
import { lazyWithRecovery } from "@/lib/lazyWithRecovery";

// Landing — STATIC import so it bundles into the entry chunk. This eliminates
// the 3-deep critical request chain (entry → Landing → PremiumHero/Nav) that
// dominates mobile LCP on the most-visited route. All other routes remain lazy.
import Landing from "./pages/Landing";


// Public pages — no auth needed
const NotFound = lazyWithRecovery("NotFound", () => import("./pages/NotFound"));
const Features = lazyWithRecovery("Features", () => import("./pages/Features"));
const Pricing = lazyWithRecovery("Pricing", () => import("./pages/Pricing"));
const Checkout = lazyWithRecovery("Checkout", () => import("./pages/Checkout"));
const CheckoutComplete = lazyWithRecovery("CheckoutComplete", () => import("./pages/CheckoutComplete"));
const CheckoutSuccess = lazyWithRecovery("CheckoutSuccess", () => import("./pages/CheckoutSuccess"));
const Legal = lazyWithRecovery("Legal", () => import("./pages/Legal"));
const FeatureDetail = lazyWithRecovery("FeatureDetail", () => import("./pages/FeatureDetail"));
const SolutionPage = lazyWithRecovery("SolutionPage", () => import("./pages/SolutionPage"));
const SolutionQuiz = lazyWithRecovery("SolutionQuiz", () => import("./pages/SolutionQuiz"));
const SolutionBuilder = lazyWithRecovery("SolutionBuilder", () => import("./pages/SolutionBuilder"));
const Solutions = lazyWithRecovery("Solutions", () => import("./pages/Solutions"));
const Industries = lazyWithRecovery("Industries", () => import("./pages/Industries"));
const IndustryPage = lazyWithRecovery("IndustryPage", () => import("./pages/IndustryPage"));
const CandidateForm = lazyWithRecovery("CandidateForm", () => import("./pages/CandidateForm"));
const PaymentPage = lazyWithRecovery("PaymentPage", () => import("./pages/PaymentPage"));
const CampaignLeadForm = lazyWithRecovery("CampaignLeadForm", () => import("./pages/CampaignLeadForm"));
const FoodPaymentPage = lazyWithRecovery("FoodPaymentPage", () => import("./pages/FoodPaymentPage"));
const CompanyProfile = lazyWithRecovery("CompanyProfile", () => import("./pages/CompanyProfile"));
const Blog = lazyWithRecovery("Blog", () => import("./pages/Blog"));
const BlogPostPage = lazyWithRecovery("BlogPost", () => import("./pages/BlogPost"));
const HelpCenter = lazyWithRecovery("HelpCenter", () => import("./pages/HelpCenter"));
const ApiDocs = lazyWithRecovery("ApiDocs", () => import("./pages/ApiDocs"));
const CaseStudies = lazyWithRecovery("CaseStudies", () => import("./pages/CaseStudies"));
const ContactSales = lazyWithRecovery("ContactSales", () => import("./pages/ContactSales"));
const Webinars = lazyWithRecovery("Webinars", () => import("./pages/Webinars"));
const SystemStatus = lazyWithRecovery("SystemStatus", () => import("./pages/SystemStatus"));
const Resources = lazyWithRecovery("Resources", () => import("./pages/Resources"));
const About = lazyWithRecovery("About", () => import("./pages/About"));
const DriverApply = lazyWithRecovery("DriverApply", () => import("./pages/driver/DriverApply"));

// Auth-dependent shell — lazy loaded so Supabase doesn't load on public pages
const AuthenticatedApp = lazyWithRecovery("AuthenticatedApp", () => import("./AuthenticatedApp"));

// UIShell — lazy with recovery
const UIShell = lazyWithRecovery("UIShell", () => import("./components/shared/UIShell"));

// QueryClient + LanguageProvider loaded lazily via wrapper
const DeferredProviders = lazyWithRecovery("DeferredProviders", () => import("./components/shared/DeferredProviders"));

// Detect if localStorage is available (blocked in sandboxed iframes)
const isLocalStorageAvailable = (() => {
  try { localStorage.setItem('__test', '1'); localStorage.removeItem('__test'); return true; } catch { return false; }
})();

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function LazyRecoveryCleanup() {
  const location = useLocation();

  useEffect(() => {
    const url = new URL(window.location.href);
    const hasRetryParams = url.searchParams.has('__lazy_retry') || url.searchParams.has('__retry');

    if (!hasRetryParams) return;

    url.searchParams.delete('__lazy_retry');
    url.searchParams.delete('__retry');
    window.history.replaceState(window.history.state, '', `${location.pathname}${url.search}${location.hash}`);
  }, [location.pathname, location.hash]);

  return null;
}

function RouteLoader() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4">
      <img src={EVOHR_LOGO} alt="EvoHR" className="h-12 w-auto animate-pulse evohr-logo-gold" />
      <div className="h-6 w-6 animate-spin rounded-full border-3 border-primary border-t-transparent" />
    </div>
  );
}

function RestaurantAliasRedirect() {
  const location = useLocation();
  const nextPath = location.pathname.replace(/^\/evo-restaurant/, "/restaurant");
  return <Navigate to={`${nextPath}${location.search}${location.hash}`} replace />;
}

function InnerApp() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTop />
      <LazyRecoveryCleanup />
      <LanguageProvider>
      <LazyErrorBoundary>
        <Suspense fallback={null}>
          <Routes>
            {/* ── Landing — skeleton already visible in HTML, no loader needed ── */}
            <Route path="/" element={<Landing />} />

            {/* ── All other routes get full providers (i18n, react-query) ── */}
            <Route path="/*" element={
              <Suspense fallback={<RouteLoader />}>
                <DeferredProviders>
                  <Routes>
                    <Route path="/features" element={<Features />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/checkout/complete" element={<CheckoutComplete />} />
                    <Route path="/checkout/success" element={<CheckoutSuccess />} />
                    <Route path="/legal" element={<Legal />} />
                    <Route path="/privacy" element={<Navigate to="/legal?section=privacy" replace />} />
                    <Route path="/terms" element={<Navigate to="/legal?section=terms" replace />} />
                    <Route path="/features/:moduleId" element={<FeatureDetail />} />
                    <Route path="/solutions" element={<Solutions />} />
                    <Route path="/solutions/quiz" element={<SolutionQuiz />} />
                    <Route path="/solutions/builder" element={<SolutionBuilder />} />
                    <Route path="/solutions/:slug" element={<SolutionPage />} />
                    <Route path="/industries" element={<Industries />} />
                    <Route path="/industries/:slug" element={<IndustryPage />} />
                    <Route path="/candidate-form/:token" element={<CandidateForm />} />
                    <Route path="/pay" element={<PaymentPage />} />
                    <Route path="/campaign/:campaignId" element={<CampaignLeadForm />} />
                    <Route path="/food-pay" element={<FoodPaymentPage />} />
                    <Route path="/company/:id" element={<CompanyProfile />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPostPage />} />
                    <Route path="/help-center" element={<HelpCenter />} />
                    <Route path="/contact-sales" element={<ContactSales />} />
                    <Route path="/api-docs" element={<ApiDocs />} />
                    <Route path="/case-studies" element={<CaseStudies />} />
                    <Route path="/webinars" element={<Webinars />} />
                    <Route path="/system-status" element={<SystemStatus />} />
                    <Route path="/resources" element={<Resources />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/driver/apply" element={<DriverApply />} />
                    <Route path="/evo-restaurant/*" element={<RestaurantAliasRedirect />} />

                    {/* ── All auth-dependent routes ── */}
                    <Route path="/*" element={<AuthenticatedApp />} />
                  </Routes>
                </DeferredProviders>
              </Suspense>
            } />
          </Routes>
        </Suspense>
      </LazyErrorBoundary>
      </LanguageProvider>
    </BrowserRouter>
  );
}

const App = () => (
  <ThemeProvider
    attribute="class"
    defaultTheme="dark"
    enableSystem={isLocalStorageAvailable}
    storageKey="evohr-theme"
  >
    <LazyErrorBoundary>
      <Suspense fallback={null}>
        <UIShell>
          <InnerApp />
        </UIShell>
      </Suspense>
    </LazyErrorBoundary>
  </ThemeProvider>
);

export default App;
